import { create } from 'zustand';
import { servicesContainer } from '@/core/di/services-container';
import {
  ApplicationType,
  PublishStatus,
  PublishResult,
  S3Environment,
  PreviewInfoResponse,
  PreviewFileInfo,
} from '../types/translation-types';
import { findMissingKeysForLanguage } from '../utils/missing-keys-utils';

interface PublishStore {
  // Data state
  publishStatus: PublishStatus | null;
  lastPublishResult: PublishResult | null;

  // Filter state
  selectedAppType: ApplicationType;

  // Loading states
  isLoading: boolean;
  isPublishing: boolean;

  // Error states
  error: string | null;
  publishError: string | null;

  // Preview state
  previewInfo: PreviewInfoResponse | null;
  previewContent: string | null;
  selectedPreviewFile: PreviewFileInfo | null;
  isLoadingPreviewInfo: boolean;
  isLoadingPreviewContent: boolean;
  isDownloadingPreview: boolean;
  previewError: string | null;

  // Missing keys modal state
  missingKeysModalOpen: boolean;
  selectedMissingLanguage: { code: string; name: string } | null;
  missingKeys: string[];
  isLoadingMissingKeys: boolean;

  // Actions
  fetchPublishStatus: () => Promise<void>;
  publish: (environment: S3Environment, notes?: string) => Promise<PublishResult>;
  setSelectedAppType: (appType: ApplicationType) => void;
  clearError: () => void;
  clearPublishError: () => void;
  reset: () => void;

  // Preview actions
  fetchPreviewInfo: () => Promise<void>;
  fetchPreviewContent: (fileKey?: string) => Promise<void>;
  downloadPreview: (fileName: string, fileKey?: string) => Promise<void>;
  setSelectedPreviewFile: (file: PreviewFileInfo | null) => void;
  clearPreviewError: () => void;
  clearPreview: () => void;

  // Missing keys modal actions
  openMissingKeysModal: (languageCode: string, languageName: string) => Promise<void>;
  closeMissingKeysModal: () => void;
}

const initialState = {
  publishStatus: null,
  lastPublishResult: null,
  selectedAppType: ApplicationType.ReactWeb,
  isLoading: false,
  isPublishing: false,
  error: null,
  publishError: null,
  // Preview initial state
  previewInfo: null,
  previewContent: null,
  selectedPreviewFile: null,
  isLoadingPreviewInfo: false,
  isLoadingPreviewContent: false,
  isDownloadingPreview: false,
  previewError: null,
  // Missing keys modal initial state
  missingKeysModalOpen: false,
  selectedMissingLanguage: null,
  missingKeys: [],
  isLoadingMissingKeys: false,
};

export const usePublishStore = create<PublishStore>((set, get) => ({
  ...initialState,

  fetchPublishStatus: async () => {
    const state = get();
    if (state.isLoading) {
      return;
    }

    set({ isLoading: true, error: null });

    try {
      const services = servicesContainer.getServices();
      const status = await services.getPublishStatusUseCase.execute(state.selectedAppType);

      set({
        publishStatus: status,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      console.error('PublishStore.fetchPublishStatus: Failed:', error);
      set({
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to load publish status',
      });
    }
  },

  publish: async (environment: S3Environment, notes?: string) => {
    const state = get();
    set({ isPublishing: true, publishError: null });

    try {
      const services = servicesContainer.getServices();
      const result = await services.publishTranslationsUseCase.execute(
        state.selectedAppType,
        environment,
        notes
      );

      // Refresh status after publish
      await get().fetchPublishStatus();

      set({
        isPublishing: false,
        lastPublishResult: result,
        publishError: null,
      });

      return result;
    } catch (error) {
      console.error('PublishStore.publish: Failed:', error);
      set({
        isPublishing: false,
        publishError: error instanceof Error ? error.message : 'Failed to publish translations',
      });
      throw error;
    }
  },

  setSelectedAppType: (appType: ApplicationType) => {
    set({
      selectedAppType: appType,
      publishStatus: null,
      // Clear preview state when app type changes
      previewInfo: null,
      previewContent: null,
      selectedPreviewFile: null,
      previewError: null,
    });
  },

  clearError: () => set({ error: null }),

  clearPublishError: () => set({ publishError: null }),

  reset: () => set(initialState),

  // Preview actions
  fetchPreviewInfo: async () => {
    const state = get();
    if (state.isLoadingPreviewInfo) {
      return;
    }

    set({ isLoadingPreviewInfo: true, previewError: null });

    try {
      const services = servicesContainer.getServices();
      const info = await services.getPreviewInfoUseCase.execute(state.selectedAppType);

      if (!info.isSuccess) {
        throw new Error(info.errorMessage || 'Failed to load preview info');
      }

      set({
        previewInfo: info,
        isLoadingPreviewInfo: false,
        previewError: null,
        // Auto-select first file for multi-file apps
        selectedPreviewFile: info.isMultiFile && info.availableFiles.length > 0
          ? info.availableFiles[0]
          : null,
      });
    } catch (error) {
      console.error('PublishStore.fetchPreviewInfo: Failed:', error);
      set({
        isLoadingPreviewInfo: false,
        previewError: error instanceof Error ? error.message : 'Failed to load preview info',
      });
    }
  },

  fetchPreviewContent: async (fileKey?: string) => {
    const state = get();
    if (state.isLoadingPreviewContent) {
      return;
    }

    set({ isLoadingPreviewContent: true, previewError: null, previewContent: null });

    try {
      const services = servicesContainer.getServices();
      const content = await services.getPreviewContentUseCase.execute(
        state.selectedAppType,
        fileKey
      );

      set({
        previewContent: content,
        isLoadingPreviewContent: false,
        previewError: null,
      });
    } catch (error) {
      console.error('PublishStore.fetchPreviewContent: Failed:', error);
      set({
        isLoadingPreviewContent: false,
        previewError: error instanceof Error ? error.message : 'Failed to load preview content',
      });
    }
  },

  downloadPreview: async (fileName: string, fileKey?: string) => {
    const state = get();
    if (state.isDownloadingPreview) {
      return;
    }

    set({ isDownloadingPreview: true, previewError: null });

    try {
      const services = servicesContainer.getServices();
      await services.downloadPreviewUseCase.execute(
        state.selectedAppType,
        fileName,
        fileKey
      );

      set({ isDownloadingPreview: false });
    } catch (error) {
      console.error('PublishStore.downloadPreview: Failed:', error);
      set({
        isDownloadingPreview: false,
        previewError: error instanceof Error ? error.message : 'Failed to download preview',
      });
    }
  },

  setSelectedPreviewFile: (file: PreviewFileInfo | null) => {
    set({ selectedPreviewFile: file, previewContent: null });
  },

  clearPreviewError: () => set({ previewError: null }),

  clearPreview: () => set({
    previewInfo: null,
    previewContent: null,
    selectedPreviewFile: null,
    previewError: null,
  }),

  // Missing keys modal actions
  openMissingKeysModal: async (languageCode: string, languageName: string) => {
    const state = get();

    // Set loading state and open modal
    set({
      isLoadingMissingKeys: true,
      selectedMissingLanguage: { code: languageCode, name: languageName },
      missingKeysModalOpen: true,
      missingKeys: [],
    });

    try {
      // Fetch all translations for the selected app type
      const services = servicesContainer.getServices();
      const response = await services.getAllTranslationsUseCase.execute(state.selectedAppType);

      // Calculate missing keys for the selected language
      const missingKeys = findMissingKeysForLanguage(response.value, languageCode);

      set({
        missingKeys,
        isLoadingMissingKeys: false,
      });
    } catch (error) {
      console.error('PublishStore.openMissingKeysModal: Failed to load missing keys:', error);
      // Keep modal open but clear loading state
      set({
        isLoadingMissingKeys: false,
        missingKeys: [],
      });
    }
  },

  closeMissingKeysModal: () => {
    set({
      missingKeysModalOpen: false,
      selectedMissingLanguage: null,
      missingKeys: [],
      isLoadingMissingKeys: false,
    });
  },
}));
